<template>
  <div :class="[$style.heroBlock, $style.config]">
    <div :class="[$style.mobile, $style.wrap]">
      <WithBackdrop
        :class="[$style.backdrop, data?.fields?.tintOverlay && $style.tint]"
        :backdrop="data?.fields?.heroBackdrop"
        :observe-intersection="false"
        fit="scale"
        eager
      >
        <AspectRatio ratio="16x9" />
        <template v-if="withNav">
          <button
            :class="[$style.button, $style.prev]"
            aria-label="previous slide"
            @click="
              () => {
                direction = 'right'
                emit('request-prev')
              }
            "
          >
            <IconChevronLeft :class="$style.icon" />
          </button>
          <button
            :class="[$style.button, $style.next]"
            aria-label="next slide"
            @click="
              () => {
                direction = 'left'
                emit('request-next')
              }
            "
          >
            <IconChevronRight :class="$style.icon" />
          </button>
        </template>
      </WithBackdrop>

      <div :class="$style.container">
        <Transition name="fade" mode="out-in">
          <div :key="data.sys.id" :class="$style.content">
            <div :class="$style.text">
              <BaseHeading :class="$style.header" size="h2">
                {{ computedHeader }}
              </BaseHeading>
              <MarkedParser :class="$style.copy" :md="data?.fields?.excerpt" />
            </div>

            <div :class="$style.ctas">
              <BaseButton
                :to="`/news/${props.data?.fields?.path}`"
                size="large"
              >
                {{ localizedStrings['read-more'] || 'Read More' }}
              </BaseButton>
            </div>
          </div>
        </Transition>
      </div>
    </div>

    <div :class="$style.desktop">
      <WithBackdrop
        :backdrop="data?.fields?.heroBackdrop"
        :class="[$style.wrap, data?.fields?.tintOverlay && $style.tint]"
        :observe-intersection="false"
        fit="scale"
        eager
      >
        <div :class="$style.container">
          <Transition
            :name="direction === 'left' ? 'fade-left' : 'fade-right'"
            mode="out-in"
          >
            <div :key="data?.sys?.id" :class="$style.content">
              <div :class="$style.text">
                <BaseHeading :class="$style.header" size="h1">
                  {{ computedHeader }}
                </BaseHeading>
                <MarkedParser
                  :class="$style.copy"
                  :md="data?.fields?.excerpt"
                />
              </div>

              <div :class="$style.ctas">
                <BaseButton
                  :to="`/news/${props.data?.fields?.path}`"
                  size="large"
                >
                  {{ localizedStrings['read-more'] || 'Read More' }}
                </BaseButton>
              </div>
            </div>
          </Transition>
          <template v-if="withNav">
            <button
              :class="[$style.button, $style.prev]"
              aria-label="previous slide"
              @click.stop="
                () => {
                  direction = 'right'
                  emit('request-prev')
                }
              "
            >
              <IconChevronLeft :class="$style.icon" />
            </button>
            <button
              :class="[$style.button, $style.next]"
              aria-label="next slide"
              @click.stop="
                () => {
                  direction = 'left'
                  emit('request-next')
                }
              "
            >
              <IconChevronRight :class="$style.icon" />
            </button>
          </template>
        </div>
      </WithBackdrop>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TypeArticleWithoutUnresolvableLinksResponse } from '~/types/contentful'

const emit = defineEmits(['request-next', 'request-prev'])

const { localizedStrings } = useLocalizedStrings()

const props = defineProps<{
  data: TypeArticleWithoutUnresolvableLinksResponse
  preheader?: string
  withNav?: boolean
}>()

const direction = useState('left')

const userStore = useUserStore()
const { displayName, loggedIn, stores } = storeToRefs(userStore)
const computedHeader = computed(() => {
  const header = props.data?.fields?.title

  // If the header has the string  {{ displayName }} we want to swap that
  // out for the username. We don't return the string till the
  // user is loaded locally.
  if (header?.includes(`{{ displayName }}`)) {
    if (!loggedIn.value) return ''

    return header?.replace(`{{ displayName }}`, displayName.value)
  }

  // If the header has the string  {{ displayName }} we want to swap that
  // out for the username. We don't return the string till the
  // user is loaded locally.
  if (header?.includes(`{{ storeName }}`)) {
    if (!stores?.value?.[0]?.name) return ''

    return header.replace(`{{ storeName }}`, stores?.value?.[0]?.name)
  }

  return header
})
</script>

<style lang="scss" module>
.config {
  --wrap-background: var(--black);
  --wrap-padding--base: 0;
  --wrap-padding--sm: 0;
  --wrap-padding--md: 60px 60px;
  --wrap-padding: var(--wrap-padding--base);

  --wrap-min-height--base: unset;
  --wrap-min-height--sm: 360px;
  --wrap-min-height--lg: 560px;
  --wrap-min-height: var(--wrap-min-height--base);

  --container-max-width: calc(1310px + 11em);

  --container-background--base: var(--black);
  --container-background--sm: unset;
  --container-background: var(--container-background--base);

  --container-padding--base: 1.5em;
  --container-padding--sm: 1em;
  --container-padding--md: 1em;
  --container-padding--lg: 1em 5em;
  --container-padding: var(--container-padding--base);

  --content-max-width: 850px;
  --content-padding: 0 2em;

  --content-margin--base: 0;
  --content-margin--md: 0 5em;
  --content-margin--lg: 0;
  --content-margin: var(--content-margin--base);

  --content-color: var(--white);
  --header-color: var(--white);
  --copy-color: var(--white);

  --ctas-margin: 1em 0 0;

  --tint-overlay: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.85) 0%,
    rgba(0, 0, 0, 0.75) 30%,
    rgba(0, 0, 0, 0) 54%
  );

  @include media('sm') {
    --wrap-padding: var(--wrap-padding--sm);
    --wrap-min-height: var(--wrap-min-height--sm);
    --container-background: var(--container-background--sm);
    --container-padding: var(--container-padding--sm);
  }

  @include media('md') {
    --wrap-padding: var(--wrap-padding--md);
    --container-padding: var(--container-padding--md);
    --content-margin: var(--content-margin--md);
  }

  @include media('lg') {
    --wrap-min-height: var(--wrap-min-height--lg);
    --container-padding: var(--container-padding--lg);
    --content-margin: var(--content-margin--lg);
  }
}

.heroBlock {
  height: 100%;
}

.wrap {
  display: grid;
  padding: var(--wrap-padding);
  min-height: var(--wrap-min-height);
  background: var(--wrap-background);
  height: 100%;
}

.tint {
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--tint-overlay);
    z-index: 0;
  }
}

.mobile {
  @include media('md') {
    display: var(--hidden-md-and-up);
  }
}

.desktop {
  display: var(--display-md-and-up);
  height: 100%;
}

.container {
  background: var(--container-background);

  display: flex;
  align-items: center;

  max-width: var(--container-max-width);
  width: 100%;

  position: relative;
  margin: 0 auto;
  padding: var(--container-padding);
}

.content {
  color: var(--content-color);
  max-width: var(--content-max-width);
  margin: var(--content-margin);
}

.header {
  color: var(--header-color);
  margin: 0 0 to-rem(9px);

  @include media('md') {
    margin: 0 0 to-rem(19px);
    text-shadow: 0 4px 6px var(--black);
  }
}

.copy {
  color: var(--copy-color);
  text-shadow: 0 2px 4px var(--black);

  p {
    font-size: to-rem(19px);
    line-height: math.div(30, 19);
    margin: 0 0 24px;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-family: var(--roboto-condensed);
    margin: 0 0 12px;
    line-height: 1;
  }

  h1,
  h2 {
    @include fluid-type(26px, 42px);
  }

  h2 {
    line-height: math.div(32, 26);
  }
}

.text {
  position: relative;
  padding: var(--content-padding);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: var(--purple-light);
  }
}

.ctas {
  margin: var(--ctas-margin);
  padding: var(--content-padding);
}

.button {
  background: rgba(black, 0.4);
  border: 2px solid white;
  border-radius: 50%;
  color: var(--white);

  display: flex;
  align-items: center;
  justify-content: center;
  width: 49px;
  height: 49px;

  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  padding: 1em;
  left: unset;
  right: unset;

  &:hover,
  &:focus {
    background: rgba(black, 0.7);
  }

  &.prev {
    left: 9px;
  }

  &.next {
    right: 9px;
  }
}

.icon {
  height: 17px;
  width: 11px;
}
</style>
